// Document management hook — unified on customer_profile_documents with PII scanning
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from './useAuth'
import { scanFileForPII } from '@/lib/pii-upload-guard'
import type { CustomerProfileDocument } from '@/types/customer'
import type { DocumentUploadFormData } from '@/types/document'
import type { PIIDetectionResult } from '@/types/pii'

const DOCS_KEY = ['customer-profile-documents']

export function useDocuments(customerId?: string) {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const [piiResult, setPiiResult] = useState<PIIDetectionResult | null>(null)
  const [piiScanError, setPiiScanError] = useState<string | null>(null)
  const [isScanning, setIsScanning] = useState(false)
  const [pendingUpload, setPendingUpload] = useState<DocumentUploadFormData | null>(null)

  const documentsQuery = useQuery({
    queryKey: [...DOCS_KEY, customerId],
    queryFn: async (): Promise<CustomerProfileDocument[]> => {
      let query = supabase
        .from('customer_profile_documents')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false })

      if (customerId) {
        query = query.eq('customer_id', customerId)
      }

      const { data, error } = await query
      if (error) throw error
      return (data ?? []) as CustomerProfileDocument[]
    },
    refetchInterval: (query) => {
      const docs = query.state.data as CustomerProfileDocument[] | undefined
      const hasProcessing = docs?.some((d) => {
        if (['completed', 'failed'].includes(d.processing_status)) return false
        // Stop polling after 5 minutes for stale pending docs
        const age = Date.now() - new Date(d.created_at).getTime()
        return age < 5 * 60 * 1000
      })
      return hasProcessing ? 5000 : false
    },
  })

  const scanAndUpload = async (formData: DocumentUploadFormData): Promise<unknown> => {
    // PII scan before upload — always show approval modal
    setIsScanning(true)
    setPiiScanError(null)

    try {
      const pii = await scanFileForPII(formData.file)
      setPiiResult(pii || { pii_found: [], total_found: 0, has_critical: false, redacted_text: '' })
      setPendingUpload(formData)
    } catch (err) {
      // PII scan failed — still show modal but with error indicator
      const errorMsg = err instanceof Error ? err.message : 'PII scan failed'
      console.error('[useDocuments] PII scan error:', err)
      setPiiScanError(errorMsg)
      setPiiResult({ pii_found: [], total_found: 0, has_critical: false, redacted_text: '' })
      setPendingUpload(formData)
    } finally {
      setIsScanning(false)
    }
    return null
  }

  const uploadAfterApproval = async () => {
    if (!pendingUpload) return
    const formData = pendingUpload
    setPiiResult(null)
    setPendingUpload(null)
    await uploadMutation.mutateAsync(formData)
  }

  const cancelPiiReview = () => {
    setPiiResult(null)
    setPendingUpload(null)
    setPiiScanError(null)
  }

  const uploadMutation = useMutation({
    mutationFn: async (formData: DocumentUploadFormData) => {
      if (!user) throw new Error('Not authenticated')

      const targetCustomerId = formData.customer_id
      if (!targetCustomerId) throw new Error('Customer is required')

      // Upload file to Supabase Storage
      const storagePath = `customers/${targetCustomerId}/${Date.now()}_${formData.file.name}`
      const { error: storageError } = await supabase.storage
        .from('customer-documents')
        .upload(storagePath, formData.file)

      if (storageError) throw new Error(`Storage upload failed: ${storageError.message}`)

      // Parse tags
      const tags = formData.tags
        ? formData.tags.split(',').map((t) => t.trim()).filter(Boolean)
        : []

      // Insert document record
      const { data: docData, error: insertError } = await supabase
        .from('customer_profile_documents')
        .insert({
          organization_id: user.organizationId,
          customer_id: targetCustomerId,
          file_name: formData.file.name,
          file_type: formData.file.type,
          file_size_bytes: formData.file.size,
          storage_path: storagePath,
          storage_bucket: 'customer-documents',
          title: formData.title,
          description: formData.description || null,
          category: formData.category,
          d365_version: formData.d365_version || null,
          d365_module: formData.d365_module || null,
          tags,
          uploaded_by: user.id,
          processing_status: 'pending',
        } as never)
        .select()
        .single()

      if (insertError) throw insertError

      // Fire-and-forget: trigger document processing for embeddings
      triggerProcessing(storagePath, targetCustomerId, user.organizationId, formData.save_to_kb !== false)

      return docData
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DOCS_KEY })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (documentId: string) => {
      if (!user) throw new Error('Not authenticated')

      // Soft-delete (set is_active = false)
      const { error } = await supabase
        .from('customer_profile_documents')
        .update({ is_active: false } as never)
        .eq('id', documentId)
        .eq('organization_id', user.organizationId)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DOCS_KEY })
    },
  })

  const retryPiiScan = async () => {
    if (!pendingUpload) return
    await scanAndUpload(pendingUpload)
  }

  return {
    documents: documentsQuery.data ?? [],
    isLoading: documentsQuery.isLoading,
    error: documentsQuery.error,
    upload: scanAndUpload,
    uploadDirect: uploadMutation.mutateAsync,
    uploadAfterApproval,
    cancelPiiReview,
    retryPiiScan,
    isUploading: uploadMutation.isPending,
    isScanning,
    piiResult,
    piiScanError,
    uploadError: uploadMutation.error,
    deleteDocument: deleteMutation.mutateAsync,
    isDeleting: deleteMutation.isPending,
  }
}

/** Fire-and-forget call to process-document edge function */
async function triggerProcessing(
  storagePath: string,
  customerId: string,
  organizationId: string,
  saveToKB = true,
): Promise<void> {
  try {
    await supabase.functions.invoke('process-document', {
      body: { filePath: storagePath, customerId, organizationId, saveToKB },
    })
  } catch (err) {
    console.warn('Document processing trigger failed (non-fatal):', err)
  }
}
